<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import { ButtonV2, Text } from '@Shelly/shelly-design-system';
	import { relayActNowCopy, relayCountdownCopy } from 'src/helpers/relay_priority_layout';
	import { to_hh_mm_ss } from 'src/helpers/utilities';

	export let remainingSeconds: number;
	export let ison: boolean;
	export let leaving = false;
	export let executing = false;
	export let disabled = false;
	export let idPrefix = 'relay-timer';

	const dispatch = createEventDispatcher<{ cancel: void; now: void }>();

	$: countdown = relayCountdownCopy(ison);
	$: actNow = relayActNowCopy(ison);
	$: displayTime = to_hh_mm_ss(Math.max(0, Math.floor(remainingSeconds)));
</script>

<div
	class="relay-timer-panel"
	class:is-leaving={leaving}
	class:is-executing={executing}
	class:is-disabled={disabled}
	aria-live="polite"
>
	<Text styleType="body-extra-small" class="relay-timer-caption">
		{$format(countdown.key, { default: countdown.default })}
	</Text>
	<strong class="relay-timer-value">{displayTime}</strong>
	<div class="relay-timer-actions">
		<ButtonV2
			id={`${idPrefix}-cancel`}
			variant="secondary"
			size="m"
			radius="extra-small"
			{disabled}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			on:click={() => dispatch('cancel')}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			id={`${idPrefix}-now`}
			variant="primary"
			size="m"
			radius="extra-small"
			{disabled}
			aria-label={$format(actNow.key, { default: actNow.default })}
			on:click={() => dispatch('now')}
		>
			{$format(actNow.key, { default: actNow.default })}
		</ButtonV2>
	</div>
</div>

<style>
	.relay-timer-panel {
		display: flex;
		min-width: 0;
		flex: 1;
		flex-direction: column;
		align-items: stretch;
		align-self: stretch;
		justify-content: center;
		gap: var(--spacing-s-plus);
		transform-origin: 0 50%;
	}

	.relay-timer-panel > :global(*) {
		opacity: 0;
		animation: relay-timer-item-in 380ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
	}

	.relay-timer-panel > :global(*:nth-child(1)) {
		animation-delay: 45ms;
	}

	.relay-timer-panel > :global(*:nth-child(2)) {
		animation-delay: 90ms;
	}

	.relay-timer-panel > :global(*:nth-child(3)) {
		animation-delay: 135ms;
	}

	.relay-timer-panel.is-leaving,
	.relay-timer-panel.is-executing {
		pointer-events: none;
	}

	.relay-timer-panel.is-leaving > :global(*) {
		animation: relay-timer-item-out 220ms cubic-bezier(0.4, 0, 1, 1) both;
	}

	.relay-timer-panel.is-leaving > :global(*:nth-child(1)) {
		animation-delay: 60ms;
	}

	.relay-timer-panel.is-leaving > :global(*:nth-child(2)) {
		animation-delay: 30ms;
	}

	.relay-timer-panel.is-leaving > :global(*:nth-child(3)) {
		animation-delay: 0ms;
	}

	.relay-timer-panel.is-executing > :global(*) {
		animation-name: relay-timer-item-execute;
		animation-duration: 280ms;
		animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	}

	.relay-timer-value {
		font-size: 22px;
		font-variant-numeric: tabular-nums;
		letter-spacing: 0.03em;
	}

	.relay-timer-panel.is-disabled {
		pointer-events: none;
	}

	.relay-timer-panel.is-disabled :global(.relay-timer-caption),
	.relay-timer-panel.is-disabled .relay-timer-value {
		color: var(--color-theme-text-disabled);
	}

	.relay-timer-actions {
		display: flex;
		flex-wrap: wrap;
		align-items: stretch;
		width: 100%;
		gap: var(--spacing-s);
	}

	.relay-timer-actions :global(.button-v2) {
		flex: 1 0 auto;
		min-width: min(100%, max-content);
		max-width: 100%;
	}

	@keyframes relay-timer-item-in {
		from {
			opacity: 0;
			transform: translateX(14px) scale(0.97);
		}
		to {
			opacity: 1;
			transform: translateX(0) scale(1);
		}
	}

	@keyframes relay-timer-item-out {
		from {
			opacity: 1;
			transform: translateX(0) scale(1);
		}
		to {
			opacity: 0;
			transform: translateX(10px) scale(0.97);
		}
	}

	@keyframes relay-timer-item-execute {
		0% {
			opacity: 1;
			transform: translateX(0) scale(1);
		}
		42% {
			opacity: 1;
			transform: translateX(-3px) scale(1.015);
		}
		100% {
			opacity: 0;
			transform: translateX(12px) scale(0.96);
		}
	}
</style>
